/**
 * Schema.org JSON-LD builders.
 *
 * Everything is emitted server-side through `head().scripts`. Google reads the
 * `JobPosting` graph for Google for Jobs (plan/v4 decision #9) and AI answer
 * engines read the rest to resolve "JobCues" as an entity.
 */
import { SITE_NAME, SITE_URL, OG_IMAGE, canonicalUrl } from "./seo";

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

/** Every external profile the brand controls. `sameAs` is how an answer engine
 *  merges those mentions into one entity. */
export const SAME_AS = [
  "https://x.com/usejobcues",
  "https://www.linkedin.com/company/jobcues",
  "https://github.com/jobcues",
];

export function organizationSchema() {
  return {
    "@type": "Organization",
    "@id": ORGANIZATION_ID,
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/icon-512.png`,
      width: 512,
      height: 512,
    },
    image: OG_IMAGE,
    description:
      "JobCues is an AI resume tailoring and job-matching platform. It rewrites a resume against a specific job description using only evidence already in the resume, scores the result against applicant tracking systems, and publishes a directory of verified tech jobs.",
    sameAs: SAME_AS,
    foundingDate: "2025",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer support",
      url: `${SITE_URL}/contact`,
      availableLanguage: ["en"],
    },
  };
}

export function websiteSchema() {
  return {
    "@type": "WebSite",
    "@id": WEBSITE_ID,
    url: SITE_URL,
    name: SITE_NAME,
    publisher: { "@id": ORGANIZATION_ID },
    inLanguage: "en",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${SITE_URL}/jobs?query={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };
}

/** Root graph. Emitted once on every page. */
export function siteGraph() {
  return {
    "@context": "https://schema.org",
    "@graph": [organizationSchema(), websiteSchema()],
  };
}

export function breadcrumbSchema(items: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: canonicalUrl(item.path),
    })),
  };
}

export function faqSchema(entries: Array<{ q: string; a: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: entries.map((e) => ({
      "@type": "Question",
      name: e.q,
      acceptedAnswer: { "@type": "Answer", text: e.a },
    })),
  };
}

type SoftwareAppInput = {
  name: string;
  description: string;
  path: string;
  /** Lowest recurring price in USD. */
  price?: string;
  featureList?: string[];
};

export function softwareApplicationSchema({
  name,
  description,
  path,
  price = "5",
  featureList,
}: SoftwareAppInput) {
  return {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name,
    url: canonicalUrl(path),
    description,
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    publisher: { "@id": ORGANIZATION_ID },
    ...(featureList ? { featureList } : {}),
    offers: {
      "@type": "Offer",
      price,
      priceCurrency: "USD",
      // Pricing lives in a section of the home page, not on its own route.
      url: `${canonicalUrl("/")}#pricing`,
      availability: "https://schema.org/InStock",
    },
  };
}

/** A generic content page. Gives AI engines a dated, attributed entity to cite. */
export function webPageSchema(input: {
  name: string;
  description: string;
  path: string;
  datePublished?: string;
  dateModified?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${canonicalUrl(input.path)}#webpage`,
    url: canonicalUrl(input.path),
    name: input.name,
    description: input.description,
    isPartOf: { "@id": WEBSITE_ID },
    publisher: { "@id": ORGANIZATION_ID },
    inLanguage: "en",
    ...(input.datePublished ? { datePublished: input.datePublished } : {}),
    ...(input.dateModified ? { dateModified: input.dateModified } : {}),
  };
}

export function datasetSchema(input: {
  name: string;
  description: string;
  path: string;
  keywords?: string[];
  dateModified?: string;
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    "@id": `${canonicalUrl(input.path)}#dataset`,
    name: input.name,
    description: input.description,
    url: canonicalUrl(input.path),
    creator: { "@id": ORGANIZATION_ID },
    publisher: { "@id": ORGANIZATION_ID },
    isAccessibleForFree: true,
    temporalCoverage: "2026",
    ...(input.keywords ? { keywords: input.keywords } : {}),
    ...(input.dateModified ? { dateModified: input.dateModified } : {}),
  };
}

